import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { CheckCircle2 } from 'lucide-react';
import TRJServicesCTA from '@/components/TRJServicesCTA';

export default function Admissions() {
  const [programs, setPrograms] = useState([]);
  const [form, setForm] = useState({ name: '', email: '', phone: '', program_interest: '', background: '', goals: '' });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => { api.get('/programs').then(({ data }) => setPrograms(data.filter(p => !p.is_coming_soon))); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post('/applications', form);
      setSent(true);
      toast.success('Application submitted.');
    } catch (err) {
      toast.error('Could not submit application');
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="admissions-page" className="bg-trjlight">
      <section className="bg-navy text-white py-20 relative overflow-hidden">
        <div className="absolute inset-0 hero-grain opacity-50" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <span className="trj-divider">Admissions</span>
          <h1 className="font-display font-semibold text-4xl lg:text-6xl mt-6 leading-tight">
            Begin with a <span className="text-gold font-serif italic">conversation.</span>
          </h1>
          <p className="mt-6 text-lg text-white/80 max-w-2xl">
            Submit your application and our Admissions team will guide you to the right program.
          </p>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-6 lg:px-8 py-16 grid lg:grid-cols-3 gap-10">
        <aside className="space-y-6">
          <div>
            <h3 className="font-display font-semibold text-navy text-xl">Application process</h3>
            <ol className="mt-4 space-y-4 text-sm">
              {['Submit your application form', 'Speak with an Admissions counselor', 'Receive enrollment confirmation', 'Begin your program — same day'].map((t, i) => (
                <li key={t} className="flex items-start gap-3">
                  <div className="h-7 w-7 rounded-full bg-navy text-white flex items-center justify-center font-semibold text-sm shrink-0">{i+1}</div>
                  <span className="text-slate-700">{t}</span>
                </li>
              ))}
            </ol>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <div className="text-xs uppercase tracking-widest text-gold font-semibold">FAQ</div>
            <div className="mt-3 space-y-3 text-sm text-slate-600">
              <p><strong className="text-navy">Do I need prior experience?</strong> No — most foundational programs are open to all learners.</p>
              <p><strong className="text-navy">Are payments required upfront?</strong> Yes, tuition is paid at enrollment via PayPal.</p>
              <p><strong className="text-navy">Is TRJ Academy accredited?</strong> TRJ Academy is an independent professional educational institution, not a college or licensing authority.</p>
            </div>
          </div>
        </aside>

        <div className="lg:col-span-2">
          {sent ? (
            <div className="bg-white border border-slate-200 rounded-xl p-10 text-center" data-testid="application-sent">
              <CheckCircle2 className="h-12 w-12 text-trjgreen mx-auto" />
              <h2 className="font-display font-semibold text-2xl text-navy mt-4">Application received</h2>
              <p className="text-slate-600 mt-2 max-w-md mx-auto">Thank you. Our Admissions team will contact you within 1–2 business days at the email you provided.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="bg-white border border-slate-200 rounded-xl p-8 space-y-5" data-testid="application-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label>Full name</Label>
                  <Input data-testid="app-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required /></div>
                <div><Label>Email</Label>
                  <Input type="email" data-testid="app-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required /></div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label>Phone</Label>
                  <Input data-testid="app-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} required /></div>
                <div>
                  <Label>Program of Interest</Label>
                  <Select value={form.program_interest} onValueChange={(v) => setForm({ ...form, program_interest: v })}>
                    <SelectTrigger data-testid="app-program"><SelectValue placeholder="Choose program" /></SelectTrigger>
                    <SelectContent>
                      {programs.map(p => <SelectItem key={p.slug} value={p.title}>{p.title}</SelectItem>)}
                      <SelectItem value="Not sure yet">Not sure yet</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Professional background</Label>
                <Textarea data-testid="app-background" rows={3} value={form.background} onChange={(e) => setForm({ ...form, background: e.target.value })} placeholder="Briefly describe your current role and experience." /></div>
              <div><Label>Career goals</Label>
                <Textarea data-testid="app-goals" rows={3} value={form.goals} onChange={(e) => setForm({ ...form, goals: e.target.value })} placeholder="What do you want to achieve with this program?" /></div>
              <Button type="submit" disabled={busy} className="btn-navy h-12 px-7" data-testid="app-submit">
                {busy ? 'Submitting…' : 'Submit Application'}
              </Button>
            </form>
          )}
        </div>
      </div>
      <TRJServicesCTA />
    </div>
  );
}
